/*
 * Copyright (c) 2019.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {NgModule} from '@angular/core';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {RouterModule, Routes} from '@angular/router';
import {AuthGuardMenuService, AuthGuardService} from 'compob2b-auth';
import {environment} from '../../environments/environment';
import {translations} from '../../environments/translations.ru';
import {CustomerResetComponent} from '../auth/reset-component/customer-reset.component';

import {LayoutComponent} from './shared/layout/layout.component';
import {NotFoundComponent} from './shared/not-found/not-found.component';
import {CustomerSignInComponent} from "./sign-in/sign-in.component";

/**
 * Routes available in cabinet module
 *
 * @usageNotes
 * path: 'cabinet/dashboard' - lazy loading of dashboard module, activating is possible
 * only with guard service
 * {@link https://angular.io/guide/router}
 * {@link https://angular.io/guide/lazy-loading-ngmodules}
 */
export const routes: Routes = [
  { path: 'sign-in', redirectTo: '/cabinet/sign-in', pathMatch: 'full'},
  { path: 'reset', redirectTo: '/cabinet/reset', pathMatch: 'full'},
  { path: 'cabinet/sign-in', component: CustomerSignInComponent, pathMatch: 'full'},
  { path: 'cabinet/reset', component: CustomerResetComponent, pathMatch: 'full'},
    {
      path: '404',
      component: NotFoundComponent,
    },

  // {
  //   path: 'cabinet/sign-up',
  //   loadComponent: () => import('../auth/sign-up-component/customer-sign-up.component').then(m => m.CustomerSignUpComponent),
  //   data: {modules: {signupFormType: SignUpBy.SMS}},
  // },
  // Sign-up form with 2 steps registration and no sms phone validation required
  {
    path: 'cabinet/sign-up',
    loadComponent: () =>
      import('../auth/sign-up-by-email-component/customer-sign-up-by-email.component')
        .then(m => m.CustomerSignUpByEmailComponent)
  },

  { path: 'sign-up', redirectTo: '/cabinet/sign-up', pathMatch: 'full'},

  {
    path: '',
    component: LayoutComponent,
    children: [
      {
        path: '',
        redirectTo: '/cabinet/dashboard',
        pathMatch: 'full',
        data: {
          breadcrumb: {label: translations.bc.home},
        },
      },
      {path: 'cabinet', redirectTo: '/cabinet/dashboard', pathMatch: 'full'},
      {path: 'cabinet/orders', redirectTo: '/cabinet/orders/products', pathMatch: 'full'},
      {path: 'cabinet/reports', redirectTo: '/cabinet/reports/orders', pathMatch: 'full'},
      {path: 'cabinet/finances', redirectTo: '/cabinet/finances/dept', pathMatch: 'full'},
      {path: 'cabinet/complaints', redirectTo: '/cabinet/complaints/standard', pathMatch: 'full'},
      {
        path: 'cabinet/news',
        redirectTo: '/cabinet/news/common',
        pathMatch: 'full',
      },
      {path: 'cabinet/files', redirectTo: '/cabinet/files/promo', pathMatch: 'full'},
      {path: 'cabinet/private', redirectTo: '/cabinet/private/info', pathMatch: 'full'},
      {path: 'cabinet/chat', redirectTo: '/cabinet/chat/standard', pathMatch: 'full'},
      {
        path: 'cabinet/dashboard',
        loadChildren: () => import('./dashboard/dashboard.module').then(m => m.DashboardModule),
        canActivate: [AuthGuardService, AuthGuardMenuService]
      },
      {
        path: 'cabinet/orders',
        loadChildren: () => import('./orders/orders.module').then(m => m.OrdersModule),
        canActivate: [AuthGuardService, AuthGuardMenuService],
        data: {breadcrumb: {skip: true}}
      },
      {
        path: 'cabinet/reports',
        loadChildren: () => import('./reports/reports.module').then(m => m.ReportsModule),
        canActivate: [AuthGuardService, AuthGuardMenuService],
        data: {breadcrumb: {skip: true}}
      },
      {
        path: 'cabinet/notifications',
        loadChildren: () => import('./notifications-module/notifications.module').then(m => m.NotificationsModule),
        canActivate: [AuthGuardService],
        data: {access: environment.notificationCustomerAccess}
      },
      {
        path: 'cabinet/finances',
        loadChildren: () => import('./finances/finances.module').then(m => m.FinancesModule),
        canActivate: [AuthGuardService, AuthGuardMenuService],
        data: {breadcrumb: {skip: true}}
      },
      {
        path: 'cabinet/news',
        loadChildren: () => import('./news/news.module').then(m => m.NewsModule),
        canActivate: [AuthGuardService],
        data: {breadcrumb: {label: translations.bc.news}}
      },
      {
        path: 'cabinet/faq',
        loadChildren: () => import('./faq/faq.module').then(m => m.FaqModule),
        canActivate: [AuthGuardService, AuthGuardMenuService],
        data: {breadcrumb: {label: translations.titles.faq}}
      },
      {
        path: 'ad-campaigns',
        loadChildren: () => import('./promotions/promotions.module').then(m => m.PromotionsModule),
        canActivate: [AuthGuardService, AuthGuardMenuService],
        data: {breadcrumb: {label: translations.bc.promotions}}
      },
      {
        path: 'cabinet/pages',
        loadChildren: () => import('./text-page/text-page.module').then(m => m.TextPageModule),
        canActivate: [AuthGuardService]
      },
      {
        path: 'cabinet/private',
        loadChildren: () => import('./private/private.module').then(m => m.PrivateModule),
        canActivate: [AuthGuardService],
        data: {breadcrumb: {skip: true}}
      },
      {
        path: 'cabinet/reset',
        component: CustomerResetComponent,
        canActivate: [AuthGuardService]
      },
      {
        path: 'subscribe-confirm/:code',
        loadChildren: () => import('./subscribe-confirm/subscribe-confirm.module').then(m => m.SubscribeConfirmModule),
        canActivate: [AuthGuardService]
      },
      {
        path: 'download',
        loadChildren: () => import('./download/download.module').then(m => m.DownloadModule),
        canActivate: [AuthGuardService]
      },
      {
        path: 'catalog',
        data: {breadcrumb: {label: translations.bc.catalog, useRouterLinkOnly: true}},
        loadChildren: () => import('./catalog/catalog.module').then(m => m.CatalogModule),
        canActivate: [AuthGuardService]
      },
      {
        path: 'product',
        loadChildren: () => import('./product/product.module').then(m => m.ProductModule),
        canActivate: [AuthGuardService]
      },
      {
        path: 'cart',
        loadChildren: () => import('./cart/cart.module').then(m => m.CartModule),
        canActivate: [AuthGuardService]
      },
      {
        path: 'cart-share/:shareSyncUid',
        loadChildren: () => import('./cart/cart.module').then(m => m.CartModule),
        canActivate: [AuthGuardService]
      },
      {
        path: '404',
        component: NotFoundComponent,
        canActivate: [AuthGuardService]
      },
      {path: '**', redirectTo: '/404'}
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes), BrowserAnimationsModule],
  exports: [RouterModule]
})
export class CabinetRoutingModule {
}
